// Validadores para formularios del sistema

import { VALIDATION_RULES } from './constants'

/**
 * Validador requerido
 * @param {string} value - Valor a validar
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const required = (value, fieldName = 'Este campo') => {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return `${fieldName} es obligatorio`
  }
  return null
}

/**
 * Validador de email
 * @param {string} value - Email a validar
 * @returns {string|null} Mensaje de error o null
 */
export const email = (value) => {
  if (!value) return null
  
  if (!VALIDATION_RULES.EMAIL_REGEX.test(value)) {
    return 'El email no es válido'
  }
  return null
}

/**
 * Validador de teléfono
 * @param {string} value - Teléfono a validar
 * @returns {string|null} Mensaje de error o null
 */
export const phone = (value) => {
  if (!value) return null
  
  if (!VALIDATION_RULES.PHONE_REGEX.test(value)) {
    return 'El teléfono no es válido'
  }
  
  const digits = value.replace(/\D/g, '')
  if (digits.length < 7 || digits.length > 15) {
    return 'El teléfono debe tener entre 7 y 15 dígitos'
  }
  
  return null
}

/**
 * Validador de CI boliviano
 * @param {string} value - CI a validar
 * @returns {string|null} Mensaje de error o null
 */
export const ci = (value) => {
  if (!value) return null
  
  const cleaned = value.replace(/\D/g, '')
  
  if (!VALIDATION_RULES.CI_REGEX.test(cleaned)) {
    return 'El CI debe tener 7 u 8 dígitos'
  }
  
  return null
}

/**
 * Validador de longitud mínima
 * @param {string} value - Valor a validar
 * @param {number} minLength - Longitud mínima
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const minLength = (value, minLength, fieldName = 'Este campo') => {
  if (!value) return null
  
  if (value.length < minLength) {
    return `${fieldName} debe tener al menos ${minLength} caracteres`
  }
  return null
}

/**
 * Validador de longitud máxima
 * @param {string} value - Valor a validar
 * @param {number} maxLength - Longitud máxima
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const maxLength = (value, maxLength, fieldName = 'Este campo') => {
  if (!value) return null
  
  if (value.length > maxLength) {
    return `${fieldName} no puede tener más de ${maxLength} caracteres`
  }
  return null
}

/**
 * Validador de contraseña
 * @param {string} value - Contraseña a validar
 * @returns {string|null} Mensaje de error o null
 */
export const password = (value) => {
  if (!value) return null
  
  if (value.length < VALIDATION_RULES.PASSWORD_MIN_LENGTH) {
    return `La contraseña debe tener al menos ${VALIDATION_RULES.PASSWORD_MIN_LENGTH} caracteres`
  }
  
  // Verificar que tenga al menos una letra mayúscula
  if (!/[A-Z]/.test(value)) {
    return 'La contraseña debe tener al menos una letra mayúscula'
  }
  
  // Verificar que tenga al menos una letra minúscula
  if (!/[a-z]/.test(value)) {
    return 'La contraseña debe tener al menos una letra minúscula'
  }
  
  // Verificar que tenga al menos un número
  if (!/\d/.test(value)) {
    return 'La contraseña debe tener al menos un número'
  }
  
  return null
}

/**
 * Validador de confirmación de contraseña
 * @param {string} value - Confirmación de contraseña
 * @param {string} password - Contraseña original
 * @returns {string|null} Mensaje de error o null
 */
export const confirmPassword = (value, password) => {
  if (!value) return null
  
  if (value !== password) {
    return 'Las contraseñas no coinciden'
  }
  return null
}

/**
 * Validador de número
 * @param {string|number} value - Valor a validar
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const number = (value, fieldName = 'Este campo') => {
  if (!value) return null
  
  if (isNaN(value) || value === '') {
    return `${fieldName} debe ser un número válido`
  }
  return null
}

/**
 * Validador de número mínimo
 * @param {string|number} value - Valor a validar
 * @param {number} min - Valor mínimo
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const min = (value, min, fieldName = 'Este campo') => {
  if (!value) return null
  
  const numValue = Number(value)
  if (isNaN(numValue)) {
    return `${fieldName} debe ser un número válido`
  }
  
  if (numValue < min) {
    return `${fieldName} debe ser mayor o igual a ${min}`
  }
  return null
}

/**
 * Validador de número máximo
 * @param {string|number} value - Valor a validar
 * @param {number} max - Valor máximo
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const max = (value, max, fieldName = 'Este campo') => {
  if (!value) return null
  
  const numValue = Number(value)
  if (isNaN(numValue)) {
    return `${fieldName} debe ser un número válido`
  }
  
  if (numValue > max) {
    return `${fieldName} debe ser menor o igual a ${max}`
  }
  return null
}

/**
 * Validador de fecha
 * @param {string|Date} value - Fecha a validar
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const date = (value, fieldName = 'Este campo') => {
  if (!value) return null
  
  const date = new Date(value)
  if (isNaN(date.getTime())) {
    return `${fieldName} debe ser una fecha válida`
  }
  return null
}

/**
 * Validador de fecha mínima
 * @param {string|Date} value - Fecha a validar
 * @param {string|Date} minDate - Fecha mínima
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const minDate = (value, minDate, fieldName = 'Este campo') => {
  if (!value) return null
  
  const date = new Date(value)
  const min = new Date(minDate)
  
  if (isNaN(date.getTime()) || isNaN(min.getTime())) {
    return `${fieldName} debe ser una fecha válida`
  }
  
  if (date < min) {
    return `${fieldName} debe ser posterior a ${minDate}`
  }
  return null
}

/**
 * Validador de fecha máxima
 * @param {string|Date} value - Fecha a validar
 * @param {string|Date} maxDate - Fecha máxima
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const maxDate = (value, maxDate, fieldName = 'Este campo') => {
  if (!value) return null
  
  const date = new Date(value)
  const max = new Date(maxDate)
  
  if (isNaN(date.getTime()) || isNaN(max.getTime())) {
    return `${fieldName} debe ser una fecha válida`
  }
  
  if (date > max) {
    return `${fieldName} debe ser anterior a ${maxDate}`
  }
  return null
}

/**
 * Validador de hora
 * @param {string} value - Hora a validar (formato HH:MM)
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const time = (value, fieldName = 'Este campo') => {
  if (!value) return null
  
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
  if (!timeRegex.test(value)) {
    return `${fieldName} debe tener el formato HH:MM`
  }
  return null
}

/**
 * Validador de hora mínima
 * @param {string} value - Hora a validar
 * @param {string} minTime - Hora mínima
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const minTime = (value, minTime, fieldName = 'Este campo') => {
  if (!value) return null
  
  const timeError = time(value, fieldName)
  if (timeError) return timeError
  
  const valueMinutes = timeToMinutes(value)
  const minMinutes = timeToMinutes(minTime)
  
  if (valueMinutes < minMinutes) {
    return `${fieldName} debe ser posterior a ${minTime}`
  }
  return null
}

/**
 * Validador de hora máxima
 * @param {string} value - Hora a validar
 * @param {string} maxTime - Hora máxima
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const maxTime = (value, maxTime, fieldName = 'Este campo') => {
  if (!value) return null
  
  const timeError = time(value, fieldName)
  if (timeError) return timeError
  
  const valueMinutes = timeToMinutes(value)
  const maxMinutes = timeToMinutes(maxTime)
  
  if (valueMinutes > maxMinutes) {
    return `${fieldName} debe ser anterior a ${maxTime}`
  }
  return null
}

/**
 * Convierte tiempo HH:MM a minutos
 * @param {string} time - Tiempo en formato HH:MM
 * @returns {number} Minutos desde medianoche
 */
const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

/**
 * Validador de URL
 * @param {string} value - URL a validar
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const url = (value, fieldName = 'Este campo') => {
  if (!value) return null
  
  try {
    new URL(value)
    return null
  } catch {
    return `${fieldName} debe ser una URL válida`
  }
}

/**
 * Validador de archivo
 * @param {File} file - Archivo a validar
 * @param {Array} allowedTypes - Tipos MIME permitidos
 * @param {number} maxSize - Tamaño máximo en bytes
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const file = (file, allowedTypes = [], maxSize = 5 * 1024 * 1024, fieldName = 'Este archivo') => {
  if (!file) return null
  
  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    return `${fieldName} debe ser de tipo: ${allowedTypes.join(', ')}`
  }
  
  if (file.size > maxSize) {
    const maxSizeMB = maxSize / (1024 * 1024)
    return `${fieldName} no puede ser mayor a ${maxSizeMB}MB`
  }
  
  return null
}

/**
 * Validador de imagen
 * @param {File} file - Archivo de imagen a validar
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const image = (file, fieldName = 'Esta imagen') => {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
  return file(file, allowedTypes, 10 * 1024 * 1024, fieldName)
}

/**
 * Validador de documento
 * @param {File} file - Archivo de documento a validar
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const document = (file, fieldName = 'Este documento') => {
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ]
  return file(file, allowedTypes, 20 * 1024 * 1024, fieldName)
}

/**
 * Validador personalizado
 * @param {any} value - Valor a validar
 * @param {Function} validator - Función de validación
 * @param {string} errorMessage - Mensaje de error
 * @returns {string|null} Mensaje de error o null
 */
export const custom = (value, validator, errorMessage) => {
  if (!value) return null
  
  if (!validator(value)) {
    return errorMessage
  }
  return null
}

/**
 * Validador de selección múltiple
 * @param {Array} value - Array de valores seleccionados
 * @param {number} minSelections - Mínimo de selecciones
 * @param {number} maxSelections - Máximo de selecciones
 * @param {string} fieldName - Nombre del campo
 * @returns {string|null} Mensaje de error o null
 */
export const multiSelect = (value, minSelections = 1, maxSelections = Infinity, fieldName = 'Este campo') => {
  if (!value || !Array.isArray(value)) {
    return `${fieldName} debe ser una selección válida`
  }
  
  if (value.length < minSelections) {
    return `Debe seleccionar al menos ${minSelections} opción(es)`
  }
  
  if (value.length > maxSelections) {
    return `No puede seleccionar más de ${maxSelections} opción(es)`
  }
  
  return null
}

/**
 * Validador de código único
 * @param {string} value - Código a validar
 * @param {Function} checkUnique - Función para verificar unicidad
 * @param {string} fieldName - Nombre del campo
 * @returns {Promise<string|null>} Mensaje de error o null
 */
export const unique = async (value, checkUnique, fieldName = 'Este campo') => {
  if (!value) return null
  
  try {
    const isUnique = await checkUnique(value)
    if (!isUnique) {
      return `${fieldName} ya existe`
    }
    return null
  } catch (error) {
    return 'Error al verificar la unicidad'
  }
}

/**
 * Combinador de validadores
 * @param {Array} validators - Array de funciones validadoras
 * @returns {Function} Función validadora combinada
 */
export const combine = (...validators) => {
  return (value, ...args) => {
    for (const validator of validators) {
      const error = validator(value, ...args)
      if (error) return error
    }
    return null
  }
}

/**
 * Validador condicional
 * @param {Function} condition - Condición para aplicar validación
 * @param {Function} validator - Validador a aplicar
 * @returns {Function} Función validadora condicional
 */
export const conditional = (condition, validator) => {
  return (value, ...args) => {
    if (condition(value, ...args)) {
      return validator(value, ...args)
    }
    return null
  }
}

// ===== VALIDADORES ESPECÍFICOS PARA REGISTRO DE ESTUDIANTES =====

/**
 * Validador de CI/Cédula de identidad
 * @param {string} value - CI a validar
 * @returns {string|null} Mensaje de error o null
 */
export const validarCI = (value) => {
  if (!value) return 'La cédula de identidad es obligatoria'
  
  const ci = value.trim()
  
  // Validar que sea un número válido
  if (!/^\d+$/.test(ci)) {
    return 'La cédula debe contener solo números'
  }
  
  // Validar longitud (7-12 dígitos típicamente)
  if (ci.length < 5 || ci.length > 20) {
    return 'La cédula debe tener entre 5 y 20 dígitos'
  }
  
  return null
}

/**
 * Validador de edad mínima para estudiantes
 * Para Kinder: mín 3-4 años
 * Para Primaria: mín 5-6 años
 * Para Secundaria: mín 12-13 años
 * @param {string} fechaNacimiento - Fecha de nacimiento (YYYY-MM-DD)
 * @param {number} edadMinima - Edad mínima requerida en años
 * @returns {string|null} Mensaje de error o null
 */
export const validarEdadMinima = (fechaNacimiento, edadMinima = 0) => {
  if (!fechaNacimiento) return 'La fecha de nacimiento es obligatoria'
  
  try {
    const nacimiento = new Date(fechaNacimiento)
    const hoy = new Date()
    
    // Calcular edad
    let edad = hoy.getFullYear() - nacimiento.getFullYear()
    const mesActual = hoy.getMonth()
    const diaActual = hoy.getDate()
    const mesMes = nacimiento.getMonth()
    const diaNac = nacimiento.getDate()
    
    if (mesActual < mesMes || (mesActual === mesMes && diaActual < diaNac)) {
      edad--
    }
    
    // Validar edad mínima
    if (edad < edadMinima) {
      return `El estudiante debe tener al menos ${edadMinima} años`
    }
    
    // Validar edad máxima (no más de 25 años)
    if (edad > 25) {
      return 'Por favor verifique la fecha de nacimiento'
    }
    
    return null
  } catch (error) {
    return 'Fecha de nacimiento inválida'
  }
}

/**
 * Validador de sexo
 * @param {string} value - Sexo a validar (M, F)
 * @returns {string|null} Mensaje de error o null
 */
export const validarSexo = (value) => {
  if (!value) return 'El sexo es obligatorio'
  if (!['M', 'F', 'Masculino', 'Femenino'].includes(value)) {
    return 'Seleccione un sexo válido'
  }
  return null
}

/**
 * Validador de relación de tutor
 * @param {string} value - Relación del tutor
 * @returns {string|null} Mensaje de error o null
 */
export const validarRelacionTutor = (value) => {
  const relacionesValidas = ['Padre', 'Madre', 'Tutor', 'Abuelo', 'Otro']
  
  if (!value) return 'La relación del tutor es obligatoria'
  if (!relacionesValidas.includes(value)) {
    return `Seleccione una relación válida: ${relacionesValidas.join(', ')}`
  }
  return null
}

/**
 * Validador de documento (formato genérico)
 * @param {File} file - Archivo a validar
 * @param {string[]} tiposPermitidos - Array de tipos MIME permitidos
 * @param {number} tamañoMaximoMB - Tamaño máximo en MB
 * @returns {string|null} Mensaje de error o null
 */
export const validarDocumento = (file, tiposPermitidos = ['application/pdf', 'image/jpeg', 'image/png'], tamañoMaximoMB = 5) => {
  if (!file) return 'El documento es obligatorio'
  
  // Validar tipo de archivo
  if (!tiposPermitidos.includes(file.type)) {
    return `El archivo debe ser uno de los siguientes tipos: ${tiposPermitidos.join(', ')}`
  }
  
  // Validar tamaño
  const tamañoMB = file.size / (1024 * 1024)
  if (tamañoMB > tamañoMaximoMB) {
    return `El archivo no debe superar ${tamañoMaximoMB} MB`
  }
  
  return null
}

/**
 * Validador de disponibilidad de cupos
 * @param {boolean} tieneCupos - Si tiene cupos disponibles
 * @param {number} cuposDisponibles - Cantidad de cupos disponibles
 * @returns {string|null} Mensaje de error o null
 */
export const validarCuposDisponibles = (tieneCupos, cuposDisponibles = 0) => {
  if (!tieneCupos || cuposDisponibles === 0) {
    return 'No hay cupos disponibles en este paralelo'
  }
  return null
}

/**
 * Validador de tutores (mínimo 1 tutor)
 * @param {Array} tutores - Array de tutores
 * @returns {string|null} Mensaje de error o null
 */
export const validarTutores = (tutores) => {
  if (!tutores || !Array.isArray(tutores)) {
    return 'Debe registrar al menos un tutor'
  }
  
  if (tutores.length === 0) {
    return 'Debe registrar al menos un tutor'
  }
  
  // Validar que haya al menos un tutor como contacto principal
  const tienePrincipal = tutores.some(t => t.es_contacto_principal === true)
  if (!tienePrincipal) {
    return 'Debe seleccionar un tutor como contacto principal'
  }
  
  return null
}

/**
 * Validador de grupo de tutores
 * @param {Array} tutores - Array de tutores
 * @returns {Object} Objeto con errores por tutor
 */
export const validarGrupoTutores = (tutores) => {
  const errores = {}
  
  if (!tutores || !Array.isArray(tutores)) {
    return { general: 'Debe registrar al menos un tutor' }
  }
  
  tutores.forEach((tutor, index) => {
    const erroresLocalles = {}
    
    if (!tutor.nombre || tutor.nombre.trim() === '') {
      erroresLocalles.nombre = 'El nombre es obligatorio'
    }
    
    if (!tutor.apellido || tutor.apellido.trim() === '') {
      erroresLocalles.apellido = 'El apellido es obligatorio'
    }
    
    if (!tutor.email || tutor.email.trim() === '') {
      erroresLocalles.email = 'El email es obligatorio'
    } else if (!VALIDATION_RULES.EMAIL_REGEX.test(tutor.email)) {
      erroresLocalles.email = 'El email no es válido'
    }
    
    if (!tutor.telefono || tutor.telefono.trim() === '') {
      erroresLocalles.telefono = 'El teléfono es obligatorio'
    }
    
    if (!tutor.relacion) {
      erroresLocalles.relacion = 'La relación es obligatoria'
    }
    
    if (Object.keys(erroresLocalles).length > 0) {
      errores[`tutor_${index}`] = erroresLocalles
    }
  })
  
  return errores
}

/**
 * Validador para el formulario completo de registro
 * @param {Object} datos - Datos del formulario
 * @returns {Object} Objeto con errores
 */
export const validarFormularioRegistro = (datos) => {
  const errores = {}
  
  // Validar datos personales del estudiante
  if (!datos.ci || datos.ci.trim() === '') {
    errores.ci = 'La cédula de identidad es obligatoria'
  }
  
  if (!datos.nombre || datos.nombre.trim() === '') {
    errores.nombre = 'El nombre es obligatorio'
  }
  
  if (!datos.apellido || datos.apellido.trim() === '') {
    errores.apellido = 'El apellido es obligatorio'
  }
  
  if (!datos.fecha_nacimiento) {
    errores.fecha_nacimiento = 'La fecha de nacimiento es obligatoria'
  } else {
    const errorEdad = validarEdadMinima(datos.fecha_nacimiento, 3)
    if (errorEdad) {
      errores.fecha_nacimiento = errorEdad
    }
  }
  
  if (!datos.sexo) {
    errores.sexo = 'El sexo es obligatorio'
  }
  
  if (!datos.direccion || datos.direccion.trim() === '') {
    errores.direccion = 'La dirección es obligatoria'
  }
  
  if (!datos.paralelo_id) {
    errores.paralelo_id = 'Debe seleccionar un paralelo'
  }
  
  // Validar tutores
  const erroresTotores = validarTutores(datos.tutores)
  if (erroresTotores) {
    errores.tutores = erroresTotores
  }
  
  return errores
}
